import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, Pencil, X } from "lucide-react";
import { useLanguage } from "../../context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("ghp-admin-token")}` });
const inputCls = "w-full border border-graphite/20 bg-white px-4 py-3 text-sm text-graphite focus:outline-none focus:border-forest transition-colors duration-200";
const labelCls = "overline-mono mb-2 block text-graphite-soft";
const TEXT_FIELDS = ["contexte", "problematique", "objectif", "partenaires", "besoins", "etapes"];
const EMPTY = {
  title_fr: "", title_en: "", statut: "idee",
  contexte_fr: "", contexte_en: "", problematique_fr: "", problematique_en: "",
  objectif_fr: "", objectif_en: "", partenaires_fr: "", partenaires_en: "",
  besoins_fr: "", besoins_en: "", etapes_fr: "", etapes_en: "",
};

const ProjectsTab = () => {
  const { t } = useLanguage();
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/projects`);
      setProjects(data);
    } catch {
      toast.error(t("forms.error"));
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/admin/projects/${editingId}`, form, { headers: authHeader() });
        toast.success(t("admin.projectUpdated"));
      } else {
        await axios.post(`${API}/admin/projects`, form, { headers: authHeader() });
        toast.success(t("admin.projectSaved"));
      }
      setForm(EMPTY);
      setEditingId(null);
      load();
    } catch {
      toast.error(t("forms.error"));
    }
    setSaving(false);
  };

  const startEdit = (p) => {
    const next = { ...EMPTY };
    Object.keys(EMPTY).forEach((k) => {
      next[k] = p[k] || (k === "statut" ? "idee" : "");
    });
    setForm(next);
    setEditingId(p.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const del = async (id) => {
    if (!window.confirm(t("admin.confirmDelete"))) return;
    try {
      await axios.delete(`${API}/admin/projects/${id}`, { headers: authHeader() });
      toast.success(t("admin.deleted"));
      load();
    } catch {
      toast.error(t("forms.error"));
    }
  };

  return (
    <div className="grid gap-10 lg:grid-cols-5" data-testid="admin-projects-tab">
      <form onSubmit={submit} className="space-y-5 border border-graphite/10 bg-white p-6 lg:col-span-2" data-testid="admin-project-form">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl font-bold tracking-tight text-graphite">
            {editingId ? t("admin.projectEdit") : t("admin.projectNew")}
          </h2>
          {editingId && (
            <button type="button" onClick={() => { setForm(EMPTY); setEditingId(null); }} className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.15em] text-graphite-soft hover:text-destructive transition-colors" data-testid="admin-project-cancel-edit">
              <X className="h-3.5 w-3.5" />
              {t("admin.cancel")}
            </button>
          )}
        </div>
        <div>
          <label className={labelCls}>{t("admin.titleFr")}</label>
          <input required className={inputCls} value={form.title_fr} onChange={(e) => setForm({ ...form, title_fr: e.target.value })} data-testid="admin-project-title-fr" />
        </div>
        <div>
          <label className={labelCls}>{t("admin.titleEn")}</label>
          <input className={inputCls} value={form.title_en} onChange={(e) => setForm({ ...form, title_en: e.target.value })} data-testid="admin-project-title-en" />
        </div>
        <div>
          <label className={labelCls}>{t("admin.status")}</label>
          <select className={inputCls} value={form.statut} onChange={(e) => setForm({ ...form, statut: e.target.value })} data-testid="admin-project-status">
            {Object.entries(t("projectStatuses")).map(([k, label]) => (
              <option key={k} value={k}>{label}</option>
            ))}
          </select>
        </div>
        {TEXT_FIELDS.map((f) => (
          <div key={f} className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>{t(`projects.labels.${f}`)} (FR)</label>
              <textarea rows={2} className={inputCls} value={form[`${f}_fr`]} onChange={(e) => setForm({ ...form, [`${f}_fr`]: e.target.value })} data-testid={`admin-project-${f}-fr`} />
            </div>
            <div>
              <label className={labelCls}>{t(`projects.labels.${f}`)} (EN)</label>
              <textarea rows={2} className={inputCls} value={form[`${f}_en`]} onChange={(e) => setForm({ ...form, [`${f}_en`]: e.target.value })} data-testid={`admin-project-${f}-en`} />
            </div>
          </div>
        ))}
        <button type="submit" disabled={saving} className="w-full bg-forest px-6 py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-white transition-colors hover:bg-graphite disabled:opacity-50" data-testid="admin-project-submit">
          {saving ? t("forms.sending") : editingId ? t("admin.update") : t("admin.publish")}
        </button>
      </form>
      <div className="space-y-4 lg:col-span-3">
        {projects.length === 0 && (
          <p className="border border-dashed border-graphite/20 py-16 text-center text-sm text-graphite-soft">{t("admin.empty")}</p>
        )}
        {projects.map((p) => (
          <article key={p.id} className="flex items-start justify-between gap-4 border border-graphite/10 bg-white p-6" data-testid={`admin-project-${p.id}`}>
            <div>
              <span className="inline-block border border-forest/40 bg-forest-soft px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-forest">
                {t(`projectStatuses.${p.statut}`)}
              </span>
              <h3 className="mt-2 font-display text-lg font-bold tracking-tight text-graphite">{p.title_fr}</h3>
              {p.problematique_fr && <p className="mt-1 text-sm text-graphite-soft">{p.problematique_fr}</p>}
            </div>
            <div className="flex shrink-0 items-center gap-3">
              <button onClick={() => startEdit(p)} aria-label={t("admin.projectEdit")} className="text-graphite/30 transition-colors hover:text-forest" data-testid={`admin-project-edit-${p.id}`}>
                <Pencil className="h-4 w-4" />
              </button>
              <button onClick={() => del(p.id)} aria-label={t("admin.delete")} className="text-graphite/30 transition-colors hover:text-destructive" data-testid={`admin-project-delete-${p.id}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
};

export default ProjectsTab;
